/**
 * context/WorkspaceContext.js
 * ────────────────────────────
 * Single source of truth for workspace state.
 * Wrap the app once — all components read from context, no duplicate fetches.
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';
import { useAuth } from './AuthContext';

const WorkspaceContext = createContext(null);

export function WorkspaceProvider({ children }) {
  const { user } = useAuth();
  const [workspaces, setWorkspaces]           = useState([]);
  const [activeWorkspace, setActiveWorkspace] = useState(null);
  const [tasks, setTasks]                     = useState([]);
  const [artifacts, setArtifacts]             = useState([]);
  const [loading, setLoading]                 = useState(true);
  const [error, setError]                     = useState(null);

  const fetchAll = useCallback(async () => {
    if (!user) return;
    try {
      setLoading(true);
      const data = await api.getWorkspaces();
      const wsList = Array.isArray(data) ? data : (data.workspaces || []);
      setWorkspaces(wsList);
      if (wsList.length > 0) {
        const ws = wsList[0];
        setActiveWorkspace(ws);
        setTasks(Array.isArray(ws.tasks) ? ws.tasks : []);
        setArtifacts(Array.isArray(ws.artifacts) ? ws.artifacts : []);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const switchWorkspace = useCallback(async (ws) => {
    setActiveWorkspace(ws);
    setTasks(Array.isArray(ws.tasks) ? ws.tasks : []);
    setArtifacts(Array.isArray(ws.artifacts) ? ws.artifacts : []);
    try {
      const fresh = await api.getWorkspace(ws.id);
      setActiveWorkspace(fresh);
      setTasks(Array.isArray(fresh.tasks) ? fresh.tasks : []);
      setArtifacts(Array.isArray(fresh.artifacts) ? fresh.artifacts : []);
    } catch { /* keep optimistic update */ }
  }, []);

  return (
    <WorkspaceContext.Provider value={{
      workspaces, activeWorkspace, tasks, artifacts,
      loading, error, switchWorkspace, refetch: fetchAll,
    }}>
      {children}
    </WorkspaceContext.Provider>
  );
}

export const useWorkspaces = () => useContext(WorkspaceContext);